import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { PriceListSummary } from '@aeris/contracts'
import { isValidPriceListCode, normalizePriceListCode } from '@aeris/pricing'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
} from '@aeris/ui'

import { ApiError, createPriceList, updatePriceList } from '../lib/api.ts'

/**
 * Create and edit a price list.
 *
 * The code is validated in the browser by the same function the server uses —
 * `@aeris/pricing` is imported by both — so a bad code is caught before a round
 * trip, and caught again server-side regardless.
 *
 * There is no currency field. Only BRL is issued, and a select with one option
 * is a control that teaches nothing; the column exists so the value need not be
 * guessed when a second currency arrives.
 */

export type PriceListFormProps = {
  /** Absent means "create". */
  priceList?: PriceListSummary | undefined
  onClose: () => void
}

export const PriceListForm = ({
  priceList,
  onClose,
}: PriceListFormProps): ReactNode => {
  const queryClient = useQueryClient()
  const editing = priceList !== undefined

  const [code, setCode] = useState(priceList?.code ?? '')
  const [name, setName] = useState(priceList?.name ?? '')
  const [isDefault, setIsDefault] = useState(priceList?.isDefault ?? false)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      const normalized = normalizePriceListCode(code)

      if (!isValidPriceListCode(normalized)) {
        throw new ApiError(
          422,
          'invalid_price_list_code',
          'Use letras, números, ponto, hífen, barra ou sublinhado.'
        )
      }
      if (name.trim().length === 0) {
        throw new ApiError(422, 'invalid_name', 'Informe o nome.')
      }

      return priceList === undefined
        ? await createPriceList({
            code: normalized,
            name: name.trim(),
            isDefault,
          })
        : await updatePriceList(priceList.id, {
            // The version this form was opened with. If somebody else saved in
            // the meantime the server refuses rather than overwriting them.
            version: priceList.version,
            code: normalized,
            name: name.trim(),
            isDefault,
          })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['price-lists'] })
      onClose()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar. Tente novamente.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={editing ? 'Editar tabela' : 'Nova tabela de preço'}
        description="Alterações são registradas na auditoria."
        className="max-w-lg"
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            mutation.mutate()
          }}
        >
          {error === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {error}
            </p>
          )}

          <Field label="Nome" required>
            {(field) => (
              <Input
                {...field}
                value={name}
                autoFocus
                onChange={(event) => {
                  setName(event.target.value)
                }}
                placeholder="Atacado"
              />
            )}
          </Field>

          <Field label="Código" required hint="Convertido para maiúsculas">
            {(field) => (
              <Input
                {...field}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value)
                }}
                placeholder="ATACADO"
                className="font-(--font-mono)"
              />
            )}
          </Field>

          <label className="flex items-center gap-2 text-(length:--text-sm) text-(--text-primary)">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(event) => {
                setIsDefault(event.target.checked)
              }}
              className="h-4 w-4"
            />
            {/* Exactly one default per tenant, held by a partial unique index.
                Marking this one demotes whichever list holds it today. */}
            Tabela padrão da conta
          </label>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              loading={mutation.isPending}
            >
              {editing ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
